import { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import { GameResult, GameType } from '../types/game.types'
import { getGameResults, saveGameResult as saveResult } from '../lib/storage'

interface AppContextType {
  gameResults: GameResult[]
  addGameResult: (result: GameResult) => void
  clearGameResults: (gameType?: GameType) => void
  getRecentResults: (gameType?: GameType, limit?: number) => GameResult[]
}

const AppContext = createContext<AppContextType | undefined>(undefined)

export function useApp() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}

interface AppProviderProps {
  children: ReactNode
}

export function AppProvider({ children }: AppProviderProps) {
  const [gameResults, setGameResults] = useState<GameResult[]>(() => {
    return getGameResults()
  })

  const addGameResult = (result: GameResult) => {
    saveResult(result)
    setGameResults(getGameResults())
  }

  const clearGameResults = (gameType?: GameType) => {
    if (gameType) {
      const filtered = gameResults.filter(r => r.gameType !== gameType)
      localStorage.setItem('game-results', JSON.stringify(filtered))
      setGameResults(filtered)
    } else {
      localStorage.removeItem('game-results')
      setGameResults([])
    }
  }

  const getRecentResults = (gameType?: GameType, limit: number = 5): GameResult[] => {
    let results = gameResults
    if (gameType) {
      results = results.filter(r => r.gameType === gameType)
    }
    return results.slice(0, limit)
  }

  // Sync with storage changes (e.g., from other tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'game-results') {
        setGameResults(getGameResults())
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  return (
    <AppContext.Provider value={{
      gameResults,
      addGameResult,
      clearGameResults,
      getRecentResults
    }}>
      {children}
    </AppContext.Provider>
  )
}